"use client";

import type { ReactNode } from "react";
import { AlertTriangle, RefreshCw, WifiOff } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { SafeErrorMessage } from "@/lib/safe-error";

type ErrorStateVariant = "inline" | "card" | "panel" | "page";

type ErrorStateProps = {
    title: string;
    description: string;
    variant?: ErrorStateVariant;
    canRetry?: boolean;
    onRetry?: () => void;
    isOffline?: boolean;
    action?: ReactNode;
    className?: string;
};

export function ErrorState({
    title,
    description,
    variant = "card",
    canRetry = true,
    onRetry,
    isOffline = false,
    action,
    className,
}: ErrorStateProps) {
    const Icon = isOffline ? WifiOff : AlertTriangle;

    const content = (
        <div className={cn("flex flex-col items-center gap-3 text-center", className)}>
            <div
                className={cn(
                    "flex items-center justify-center rounded-full border border-border/60 bg-muted/30",
                    variant === "inline" ? "size-10" : "size-12",
                )}
            >
                <Icon
                    className={cn(
                        "text-muted-foreground",
                        variant === "inline" ? "size-5" : "size-6",
                    )}
                    aria-hidden
                />
            </div>
            <div className="space-y-1.5">
                <p
                    className={cn(
                        "font-medium text-foreground",
                        variant === "inline" ? "text-sm" : "text-base",
                    )}
                >
                    {title}
                </p>
                <p className="max-w-prose text-sm leading-relaxed text-muted-foreground text-pretty">
                    {description}
                </p>
            </div>
            {(canRetry && onRetry) || action ? (
                <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                    {canRetry && onRetry ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={onRetry}
                        >
                            <RefreshCw data-icon="inline-start" />
                            Try again
                        </Button>
                    ) : null}
                    {action}
                </div>
            ) : null}
        </div>
    );

    if (variant === "inline") {
        return (
            <div
                className={cn(
                    "flex min-h-[120px] items-center justify-center rounded-lg border border-dashed border-border/80 px-4 py-6",
                    className,
                )}
                role="alert"
            >
                {content}
            </div>
        );
    }

    if (variant === "page") {
        return (
            <div
                className={cn(
                    "relative flex min-h-[50vh] flex-1 items-center justify-center p-6",
                    className,
                )}
                role="alert"
            >
                <div className="app-panel w-full max-w-md p-8">{content}</div>
            </div>
        );
    }

    const panelClass =
        variant === "panel"
            ? "app-panel flex min-h-[200px] flex-1 items-center justify-center p-8"
            : "app-panel flex min-h-[160px] items-center justify-center p-6";

    return (
        <div className={cn(panelClass, className)} role="alert">
            {content}
        </div>
    );
}

/** Build ErrorState props from a SafeErrorMessage. */
export function errorStateFromSafeMessage(
    message: SafeErrorMessage,
    overrides?: Partial<ErrorStateProps>,
): ErrorStateProps {
    return {
        title: message.title,
        description: message.description,
        canRetry: message.canRetry,
        isOffline: message.isOffline,
        ...overrides,
    };
}

type PageLoadErrorProps = {
    message: SafeErrorMessage;
    onRetry?: () => void;
};

/** Full-page error for route-level failures (used by AppErrorBoundary). */
export function PageLoadError({ message, onRetry }: PageLoadErrorProps) {
    return (
        <ErrorState
            variant="page"
            title={message.title}
            description={message.description}
            canRetry={message.canRetry}
            isOffline={message.isOffline}
            onRetry={onRetry}
            action={
                <Button asChild variant="ghost" size="sm">
                    <Link to="/">Back to dashboard</Link>
                </Button>
            }
        />
    );
}
